import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Config } from '../config';

/**
 * Two-Factor Authentication settings section.
 * Handles enable flow (QR display + code verification) and disable flow (password confirmation).
 */
function TwoFactorSection() {
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [mode, setMode] = useState('idle'); // 'idle' | 'setup' | 'disable'
  const [setupData, setSetupData] = useState(null); // {secret, qr_code_base64, provisioning_uri}
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { 'access_token': `Bearer ${token}` };

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${Config.APIBaseURL}/user/2fa/status`, {
        headers,
        withCredentials: true
      });
      setEnabled(!!res.data.enabled);
    } catch (e) {
      setError('Failed to load 2FA status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const startSetup = async () => {
    setError(null);
    setSuccess(null);
    setSubmitting(true);
    try {
      const res = await axios.get(`${Config.APIBaseURL}/user/2fa/setup`, {
        headers,
        withCredentials: true
      });
      setSetupData(res.data);
      setMode('setup');
      setCode('');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to start 2FA setup');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmEnable = async () => {
    if (!code || code.length !== 6) {
      setError('Please enter the 6-digit code from your authenticator app.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await axios.post(`${Config.APIBaseURL}/user/2fa/enable`, { code }, {
        headers,
        withCredentials: true
      });
      setEnabled(true);
      setMode('idle');
      setSetupData(null);
      setCode('');
      setSuccess('Two-factor authentication enabled successfully.');
    } catch (e) {
      setError(e.response?.data?.detail || 'Invalid verification code.');
    } finally {
      setSubmitting(false);
    }
  };

  const startDisable = () => {
    setMode('disable');
    setPassword('');
    setError(null);
    setSuccess(null);
  };

  const confirmDisable = async () => {
    if (!password) {
      setError('Please enter your password.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await axios.post(`${Config.APIBaseURL}/user/2fa/disable`, { password }, {
        headers,
        withCredentials: true
      });
      setEnabled(false);
      setMode('idle');
      setPassword('');
      setSuccess('Two-factor authentication disabled.');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to disable 2FA.');
    } finally {
      setSubmitting(false);
    }
  };

  const cancel = () => {
    setMode('idle');
    setSetupData(null);
    setCode('');
    setPassword('');
    setError(null);
  };

  return (
    <div className="bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 mt-6">
      <div className="px-6 py-5 border-b border-slate-200 dark:border-slate-700">
        <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
          Two-Factor Authentication
        </h2>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Add an extra layer of security to your account using an authenticator app (Google Authenticator, Authy, 1Password, etc.).
        </p>
      </div>

      <div className="p-6">
        {loading ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">Loading...</p>
        ) : (
          <>
            {/* Status */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <span className={`inline-block w-2.5 h-2.5 rounded-full ${enabled ? 'bg-green-500' : 'bg-slate-400'}`}></span>
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  {enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              {mode === 'idle' && (
                enabled ? (
                  <button
                    type="button"
                    onClick={startDisable}
                    className="btn border border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 text-sm"
                  >
                    Disable 2FA
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={startSetup}
                    disabled={submitting}
                    className="btn bg-indigo-500 hover:bg-indigo-600 text-white text-sm disabled:opacity-50"
                  >
                    {submitting ? 'Setting up...' : 'Enable 2FA'}
                  </button>
                )
              )}
            </div>

            {/* Success message */}
            {success && (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded p-3 mb-4">
                <p className="text-sm text-green-700 dark:text-green-400">{success}</p>
              </div>
            )}

            {/* Error message */}
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-3 mb-4">
                <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
              </div>
            )}

            {/* Setup flow */}
            {mode === 'setup' && setupData && (
              <div className="border border-slate-200 dark:border-slate-700 rounded p-4 bg-slate-50 dark:bg-slate-900/40">
                <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3">
                  Scan this QR code with your authenticator app
                </h3>
                <div className="flex flex-col sm:flex-row gap-6 items-start">
                  <div className="flex-shrink-0 bg-white p-2 rounded border border-slate-200">
                    <img
                      src={setupData.qr_code_base64}
                      alt="2FA QR Code"
                      className="w-44 h-44"
                    />
                  </div>
                  <div className="flex-1 space-y-3">
                    <div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">
                        Can't scan? Enter this code manually:
                      </p>
                      <code className="block text-xs font-mono bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-2 py-1.5 break-all">
                        {setupData.secret}
                      </code>
                    </div>
                    <div>
                      <label htmlFor="totp_verify" className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                        Enter the 6-digit code from your app to confirm
                      </label>
                      <input
                        id="totp_verify"
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={code}
                        onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                        className="form-input w-full text-center font-mono tracking-widest text-lg"
                        placeholder="000000"
                        autoFocus
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={confirmEnable}
                        disabled={submitting || code.length !== 6}
                        className="btn bg-indigo-500 hover:bg-indigo-600 text-white text-sm disabled:opacity-50"
                      >
                        {submitting ? 'Verifying...' : 'Confirm & Enable'}
                      </button>
                      <button
                        type="button"
                        onClick={cancel}
                        className="btn border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Disable flow */}
            {mode === 'disable' && (
              <div className="border border-slate-200 dark:border-slate-700 rounded p-4 bg-slate-50 dark:bg-slate-900/40">
                <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3">
                  Confirm your password to disable 2FA
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                  Warning: disabling 2FA will make your account less secure.
                </p>
                <div className="space-y-3">
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="form-input w-full"
                    placeholder="Current password"
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={confirmDisable}
                      disabled={submitting || !password}
                      className="btn bg-red-500 hover:bg-red-600 text-white text-sm disabled:opacity-50"
                    >
                      {submitting ? 'Disabling...' : 'Disable 2FA'}
                    </button>
                    <button
                      type="button"
                      onClick={cancel}
                      className="btn border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default TwoFactorSection;
